import { ISimpleFile } from '@/hooks/useDownloadSelectedFiles'
import { contentTypes } from '@/kontent/project/contentTypes'
import { taxonomies } from '@/kontent/project/taxonomies'
import type { TaxoResourceType, TaxoSyllabus } from '@/kontent/taxonomies'
import type { Mapping, TaxoStageWithLifeSkill } from '@/types'
import { TDownloadListFile } from '@/types/downloadList'
import { FetchResourcesResponseData } from '@/types/fetchResourcesFromAzure'
import { extractFilenameFromUrl } from '@/utils/getFilesizeFormatter'
import { getUrlFromFileObj } from '@/utils/getUrlFromFileObj'
import {
	isAssetWithRawElement,
	isResourceContainer,
	isWebLinkVideoOrExtOrTeachingAdviceExtended,
} from '@/utils/type_predicates'
import { sendGTMEvent } from '@next/third-parties/google'

// Interface specifically for GTM tracking
export interface IGtmTrackingResources {
	system: {
		id: string
		codename: string
		type: string
	}
	title: string
	resource_type: {
		name: string
		codename: string
	}
	url: string
	syllabuses: Array<{
		name: string
		codename: string
		taxonomy: string
	}>
	stages: Array<{
		name: string
		codename: string
	}>
	years?: Array<{
		name: string
		codename: string
	}>
	syllabus_types: Array<{
		name: string
		codename: string
	}>
	officialreleasedate: string
	filesize: number
}

/**
 * Helper function to detect if current page is on Assessment tab
 * @param asPath - The router.asPath or current URL path
 * @returns True if on Assessment tab, false otherwise
 */
export const isAssessmentTab = (asPath: string): boolean => {
	const pathSegments = asPath.split('?')[0].split('/').filter(Boolean)
	return (
		pathSegments[0] === 'learning-areas' && pathSegments[3] === 'assessment'
	)
}
export interface ResourceEventContext {
	resource_type_title: string
	resource_type_codename: string
	syllabus: string | '[Multiple]'
	syllabus_type_mainstream: boolean
	syllabus_type_lifeskills: boolean
	early_stage_1?: boolean
	stage_1?: boolean
	stage_2?: boolean
	stage_3?: boolean
	stage_4?: boolean
	stage_5?: boolean
	stage_6?: boolean
}

export interface ResourceTrackingData {
	id: string
	name?: string
	filename?: string
	contentType?: string
	fileSize?: number
	url?: string
	resourceType?: TaxoResourceType | string
	resourceTypeTitle?: string
	stages?: TaxoStageWithLifeSkill[] | string[]
	syllabuses?: { taxonomy: TaxoSyllabus | string; name: string }[]
	syllabus_types?: string[]
	context?: ResourceEventContext
}

export interface ResourceEventData {
	resource_id: string
	item_name: string
	content_type: string
	file_size?: number
	file_name?: string
	url: string
	context: ResourceEventContext
}

export interface GTMEventData {
	event: 'resource'
	event_data: {
		page_type: string
		page_url: string
		page_query?: string
		file_size?: number
		file_name?: string
		resources: ResourceEventData[]
	}
}

/**
 * Helper function to get taxonomy name from codename
 * @param codename - The codename of the taxonomy term
 * @returns The name of the taxonomy term, or the codename if not found
 */
const getTaxonomyNameFromCodename = (codename: string): string => {
	const term =
		taxonomies.resource_type.terms[
			codename as keyof typeof taxonomies.resource_type.terms
		]
	return term?.name || codename
}

/**
 * Helper function to get syllabus display name from codename
 * @param codename - The codename of the syllabus taxonomy term
 * @returns The display name of the syllabus, or the codename if not found
 */
export const getSyllabusNameFromCodename = (codename: string): string => {
	const term =
		taxonomies.syllabus.terms[
			codename as keyof typeof taxonomies.syllabus.terms
		]
	return term?.name || codename
}

// NOTE: we can possibly use this function in all hardcoded `pageType` declaration for GTM tracking

/**
 * Helper function to get the human-readable name from contentTypes based on codename
 * @param codename - The codename of the content type
 * @returns The human-readable name without emoji icons, or undefined if not found
 */
export const getPageTypeName = (codename: string): string | undefined => {
	const contentType = contentTypes[codename as keyof typeof contentTypes]
	const name = contentType?.name
	if (!name) return undefined
	// Remove emoji icons from the beginning of the string
	return name.replace(/^[^\w\s]*\s*/, '').trim()
}

export const transformAzureResourceForTracking = (
	resourceData: FetchResourcesResponseData,
): ResourceTrackingData => {
	return {
		id: resourceData.system?.id,
		name: resourceData.title,
		filename:
			extractFilenameFromUrl(resourceData.url) || resourceData.title,
		contentType: resourceData.system?.type,
		fileSize: resourceData.filesize,
		url: resourceData.url,
		resourceType: resourceData.resource_type?.codename,
		resourceTypeTitle: resourceData.resource_type?.name,
		stages: resourceData.stages?.map((s) => s.codename),
		syllabuses: resourceData.syllabuses?.map((s) => ({
			taxonomy: s.codename,
			name: s.name,
		})),
		syllabus_types: resourceData.syllabus_types?.map((st) => st.codename),
	}
}

export const transformGtmTrackingResourcesForTracking = (
	resourceDataArray: IGtmTrackingResources[],
): ResourceTrackingData[] => {
	return resourceDataArray.map((data) => ({
		id: data.system.id,
		name: data.title,
		filename: extractFilenameFromUrl(data.url) || data.title,
		contentType: data.system.type,
		fileSize: data.filesize,
		url: data.url,
		resourceType: data.resource_type.codename,
		resourceTypeTitle: data.resource_type.name,
		stages: data.stages.map((stage) => stage.codename),
		syllabuses: data.syllabuses.map((syllabus) => ({
			taxonomy: syllabus.codename,
			name: syllabus.name,
		})),
		syllabus_types: data.syllabus_types.map((type) => type.codename),
	}))
}

export const transformLegacyFileForTracking = (
	file: TDownloadListFile,
	mappings: Mapping[],
): ResourceTrackingData => {
	const url = getUrlFromFileObj(file, mappings) || ''

	// Extract rich metadata from TDownloadListFile
	let stages: any[] = []
	let syllabuses: any[] = []
	let syllabus_types: string[] = []
	let resourceType: string | undefined = undefined
	let resourceTypeTitle: string | undefined = undefined
	const contentType = (file as any).system?.type || 'unknown'

	if (isAssetWithRawElement(file)) {
		// For AssetWithRawElement type
		stages = file.stage?.map((s) => s.codename) || []
		syllabuses =
			file.syllabus?.map((s) => ({
				taxonomy: s.codename,
				name: s.name,
			})) || []
		resourceType = file.resource_type?.[0]?.codename
		resourceTypeTitle = file.resource_type?.[0]?.name
		// Determine syllabus types based on syllabus codenames
		syllabus_types =
			file.syllabus?.map((s) => {
				if (s.codename?.includes('life_skills')) return 'life_skills'
				return 'mainstream'
			}) || []
	} else if (
		isWebLinkVideoOrExtOrTeachingAdviceExtended(file) ||
		isResourceContainer(file)
	) {
		// For Kontent content items - safer type access
		try {
			stages =
				file.elements.stages__stages.value.map(
					({ codename }) => codename,
				) || []

			// Safe access to elements with proper type checking
			const fileWithElements = file as any
			if (fileWithElements.elements?.syllabus?.value) {
				syllabuses = fileWithElements.elements.syllabus.value.map(
					(s: any) => ({
						taxonomy: s.codename,
						name: s.name,
					}),
				)
			}

			if (fileWithElements.elements?.resource_type?.value?.[0]) {
				resourceType =
					fileWithElements.elements.resource_type.value[0].codename
				resourceTypeTitle =
					fileWithElements.elements.resource_type.value[0].name
			}

			// Determine syllabus types
			if (fileWithElements.elements?.syllabus?.value) {
				syllabus_types = fileWithElements.elements.syllabus.value.map(
					(s: any) => {
						if (s.codename?.includes('life_skills'))
							return 'life_skills'
						return 'mainstream'
					},
				)
			}
		} catch (error) {
			// Fallback to empty data if type access fails
			console.warn('Failed to extract metadata from Kontent item:', error)
		}
	}

	// Extract ID with fallback logic
	const id = isAssetWithRawElement(file)
		? file.id
		: file.system?.id || file.system?.codename || 'unknown'

	// Extract name with proper fallback logic for tracking
	let name = 'Unknown Resource'
	let filename = 'Unknown Resource'

	if (isAssetWithRawElement(file)) {
		name = (file.title || file.fileName || '').trim()
		filename = name
	} else if (isResourceContainer(file)) {
		// For resource containers, extract filename from URL like Azure version
		let extractedFilename = undefined
		if (url) {
			extractedFilename = extractFilenameFromUrl(url)
		}

		// Use the actual resource title for item_name, not system type
		name =
			file.elements.title?.value ||
			file.elements.file_name?.value ||
			file.elements.asset?.value?.[0]?.name ||
			file.system.name ||
			'Unknown Resource'
		name = name.trim()

		// Use extracted filename from URL for file_name to match Azure behavior
		filename = extractedFilename || name
	} else {
		// For other types, try elements.title.value or system.name
		name = (
			file.elements.title?.value ||
			file.system.name ||
			'Unknown Resource'
		).trim()
		filename = name
	}

	return {
		id,
		name,
		filename,
		contentType,
		fileSize: isAssetWithRawElement(file)
			? file.size
				? parseInt(String(file.size))
				: undefined
			: isResourceContainer(file)
			? file.elements.asset.value?.[0].size
				? parseInt(String(file.elements.asset.value[0].size))
				: undefined
			: undefined,
		url: url,
		resourceType,
		resourceTypeTitle,
		stages,
		syllabuses,
		syllabus_types,
	}
}

export const transformSimpleFilesForTracking = (
	files: ISimpleFile[],
): IGtmTrackingResources[] => {
	return files.map((file) => ({
		system: {
			id: file.id,
			codename: file.id,
			type: file.content_type,
		},
		title: file.title,
		resource_type: {
			name: getTaxonomyNameFromCodename(file.resource_type || ''),
			codename: file.resource_type,
		},
		url: file.url || '#',
		syllabuses: (file.syllabuses || []).map((s) => ({
			name: getSyllabusNameFromCodename(s),
			codename: s,
			taxonomy: s,
		})),
		stages: (file.stages || []).map((s) => ({
			name: s,
			codename: s,
		})),
		years: (file.years || []).map((y) => ({
			name: y,
			codename: y,
		})),
		syllabus_types: (file.syllabus_types || []).map((st) => ({
			name: st,
			codename: st,
		})),
		officialreleasedate:
			file.officialreleasedate || new Date().toISOString(),
		filesize: file.filesize || 0,
	}))
}

/**
 * Universal resource tracking utility resource interaction events
 * Content type and interaction type information is passed from the consumer level
 * This is the single entry point for all resource tracking events
 */
export function trackUniversalResourceEvent(
	trackAfterDownload: boolean = false,
	resourcesArray: ResourceTrackingData[],
	pageType?: string,
	pageUrl?: string,
	pageQuery?: string,
	syllabusContext?: {
		codename?: string
		isLifeSkills?: boolean
		isMainstream?: boolean
	},
	totalFileSize?: number,
) {
	if (!resourcesArray.length) return
	if (!pageType) {
		console.warn(
			'trackUniversalResourceEvent: pageType is required but was not provided',
		)
		return
	}

	// Transform resources into the required format
	const formattedResources: ResourceEventData[] = resourcesArray.map(
		(resource) => {
			let syllabusName = '[Multiple]'
			let syllabusTypeMainstream = false
			let syllabusTypeLifeskills = false

			// Determine syllabus name
			if (resource.syllabuses && resource.syllabuses.length === 1) {
				syllabusName = resource.syllabuses[0].name
			} else if (resource.context?.syllabus) {
				syllabusName = resource.context.syllabus
			}

			// Set syllabus type flags from resource data
			if (resource.syllabus_types) {
				syllabusTypeMainstream =
					resource.syllabus_types.includes('mainstream')
				syllabusTypeLifeskills =
					resource.syllabus_types.includes('life_skills')
			}

			// Override with syllabusContext if provided (page-level context takes precedence)
			if (syllabusContext) {
				if (syllabusContext.isLifeSkills !== undefined) {
					syllabusTypeLifeskills = syllabusContext.isLifeSkills
				}
				if (syllabusContext.isMainstream !== undefined) {
					syllabusTypeMainstream = syllabusContext.isMainstream
				}
			}

			// Use context values if they exist
			if (resource.context?.syllabus_type_mainstream !== undefined) {
				syllabusTypeMainstream =
					resource.context.syllabus_type_mainstream
			}
			if (resource.context?.syllabus_type_lifeskills !== undefined) {
				syllabusTypeLifeskills =
					resource.context.syllabus_type_lifeskills
			}

			// Determine stages - only include the ones that are true
			const stageFlags: Record<string, boolean> = {}

			// Process stages from resource stages array
			if (resource.stages) {
				resource.stages.forEach((stage) => {
					const stageStr = String(stage)

					// Handle life skills stages
					if (stageStr.includes('life_skills@primary')) {
						stageFlags.stage_ls_primary = true
					} else if (stageStr.includes('life_skills@secondary')) {
						stageFlags.stage_ls_secondary = true
					} else if (stageStr.includes('life_skills@senior')) {
						stageFlags.stage_ls_senior = true
					} else {
						// Use stage codenames exactly as they are
						stageFlags[stageStr] = true
					}
				})
			}

			// Override with stages from context if they exist
			if (resource.context) {
				Object.keys(resource.context).forEach((key) => {
					if (
						(key.startsWith('stage_') ||
							key.startsWith('early_stage_')) &&
						resource.context[key]
					) {
						stageFlags[key] = true
					}
				})
			}

			// Build context object with only relevant properties
			const context: ResourceEventContext = {
				syllabus: syllabusName,
				resource_type_title: '',
				resource_type_codename: '',
				syllabus_type_mainstream: syllabusTypeMainstream,
				syllabus_type_lifeskills: syllabusTypeLifeskills,
			}

			// Add resource type information
			if (
				resource.resourceType ||
				resource.context?.resource_type_codename
			) {
				context.resource_type_codename =
					resource.resourceType ||
					resource.context.resource_type_codename
			}

			// Add resource_type_title - use existing title or get from taxonomy
			if (
				resource.resourceTypeTitle ||
				resource.context?.resource_type_title
			) {
				context.resource_type_title =
					resource.resourceTypeTitle ||
					resource.context.resource_type_title
			} else if (
				resource.resourceType ||
				resource.context?.resource_type_codename
			) {
				// If we have a codename but no title, get the name from taxonomies
				context.resource_type_title = getTaxonomyNameFromCodename(
					resource.resourceType ||
						resource.context.resource_type_codename,
				)
			}

			// Add only the stage flags that are true
			Object.entries(stageFlags).forEach(([key, value]) => {
				if (value) {
					context[key] = true
				}
			})

			// Build the resource data object
			const resourceData: ResourceEventData = {
				resource_id: resource.id,
				item_name: resource.name || 'Unknown Resource',
				content_type: resource.contentType || 'unknown',
				url: resource.url || '',
				context,
			}

			// Only add file_size for resource_container or asset content types
			if (
				resource.contentType === 'resource_container' ||
				resource.contentType === 'asset'
			) {
				resourceData.file_size = resource.fileSize
			}

			return resourceData
		},
	)

	// Create the event data payload
	const eventDataPayload: {
		page_type: string
		page_url: string
		page_query?: string
		file_size?: number
		file_name?: string
		resources: ResourceEventData[]
	} = {
		page_type: pageType,
		page_url: pageUrl?.split('?')[0] || '',
		page_query: pageUrl?.includes('?') ? pageUrl.split('?')[1] : pageQuery,
		resources: formattedResources,
	}

	// Add file_size only for download events
	if (trackAfterDownload && totalFileSize !== undefined) {
		eventDataPayload.file_size = totalFileSize
	}

	// Remove undefined values from payload
	Object.keys(eventDataPayload).forEach((key) => {
		if (
			eventDataPayload[key as keyof typeof eventDataPayload] === undefined
		) {
			delete eventDataPayload[key as keyof typeof eventDataPayload]
		}
	})

	// Create the GTM event with event_data wrapper
	const eventData: GTMEventData = {
		event: 'resource',
		event_data: eventDataPayload,
	}

	// Send an empty event to GTM
	sendGTMEvent({ event: undefined, event_data: undefined })
	// Send to GTM
	sendGTMEvent(eventData)

	// Also log for debugging in development
	if (process.env.NODE_ENV === 'development') {
		console.log('GTM Event Sent:', eventData)
	}
}
